/**
 * Pure helpers that split long documents (rules, legal copy) into bounded,
 * readable pages. Nothing is ever dropped: every block of text lands on
 * exactly one page, in order. Very long paragraphs are split at sentence
 * boundaries into "continued" blocks.
 */

export interface DocInline {
  text: string;
  /** http(s) / mailto / in-document `#anchor` links only. */
  href?: string;
  strong?: boolean;
}

export type DocBlockKind = 'title' | 'subtitle' | 'h1' | 'h2' | 'p' | 'li';

export interface DocBlock {
  kind: DocBlockKind;
  inlines: DocInline[];
  /** Element ids / `<a name>` anchors that point at this block. */
  anchors?: string[];
  /** Set on the 2nd+ piece of a paragraph that was split across pages. */
  continued?: boolean;
}

export const blockText = (block: DocBlock) =>
  block.inlines.map(i => i.text).join('');

const isHeading = (block: DocBlock) =>
  block.kind === 'h1' || block.kind === 'h2' || block.kind === 'title';

/** Approximate size of a block in "body characters", including its margins. */
export const blockWeight = (block: DocBlock) => {
  const len = blockText(block).length;
  switch (block.kind) {
    case 'title':
      return len * 1.8 + 90;
    case 'h1':
      return len * 1.3 + 80;
    case 'h2':
      return len * 1.15 + 60;
    case 'li':
      return len + 50;
    default:
      return len + 40;
  }
};

/**
 * How many body characters fit in a box. Deliberately conservative so that
 * the measured page almost never needs its own (safety-net) scroll.
 */
export const estimateCharBudget = (
  width: number,
  height: number,
  { fontPx = 16, lineHeight = 1.55, fill = 0.78 } = {}
) => {
  if (!width || !height) return 900;
  const charsPerLine = Math.max(16, Math.floor(width / (fontPx * 0.56)));
  const lines = Math.max(3, Math.floor(height / (fontPx * lineHeight)));
  return Math.max(220, Math.floor(charsPerLine * lines * fill));
};

/** Split without changing a single character, including boundary whitespace. */
export const splitText = (text: string, max: number): string[] => {
  const limit = Math.max(1, Math.floor(max));
  const out: string[] = [];
  let remaining = text;
  while (remaining.length > limit) {
    const prefix = remaining.slice(0, limit);
    const boundaries = Array.from(
      prefix.matchAll(/(?:[.!?;:]["')\]]*\s+|\s+)/g)
    );
    const boundary = boundaries[boundaries.length - 1];
    const end = boundary ? boundary.index! + boundary[0].length : limit;
    out.push(remaining.slice(0, end));
    remaining = remaining.slice(end);
  }
  if (remaining) out.push(remaining);
  return out;
};

/**
 * Splits a block whose weight exceeds `max` into several blocks. Links are
 * kept intact; text runs (plain or bold) are cut at sentence boundaries.
 */
export const splitBlock = (block: DocBlock, max: number): DocBlock[] => {
  if (blockWeight(block) <= max || isHeading(block)) return [block];
  const limit = Math.max(120, max - 60);
  const pieces: DocInline[][] = [[]];
  let size = 0;
  block.inlines.forEach(inline => {
    const parts = inline.href
      ? [inline.text]
      : splitText(inline.text, Math.max(60, limit));
    parts.forEach(part => {
      if (size + part.length > limit && size > 0) {
        pieces.push([]);
        size = 0;
      }
      const text = part;
      pieces[pieces.length - 1].push({ ...inline, text });
      size += part.length;
    });
  });
  return pieces
    .filter(p => p.some(i => i.text.trim()))
    .map((inlines, i) => ({
      ...block,
      inlines,
      anchors: i === 0 ? block.anchors : undefined,
      continued: i > 0 ? true : block.continued,
    }));
};

/**
 * Greedy pagination. A heading never ends a page (it moves to the next one),
 * and a top-level heading starts a fresh page when the current one is already
 * mostly full.
 */
export const paginateBlocks = (
  blocks: DocBlock[],
  budget: number
): DocBlock[][] => {
  const max = Math.max(160, budget);
  const pieces = blocks.flatMap(b => splitBlock(b, max));
  const pages: DocBlock[][] = [];
  let page: DocBlock[] = [];
  let used = 0;

  const flush = () => {
    if (!page.length) return;
    // Don't strand headings at the bottom of a page.
    const carry: DocBlock[] = [];
    while (page.length > 1 && isHeading(page[page.length - 1])) {
      carry.unshift(page.pop() as DocBlock);
    }
    pages.push(page);
    page = carry;
    used = carry.reduce((sum, b) => sum + blockWeight(b), 0);
  };

  pieces.forEach(block => {
    const w = blockWeight(block);
    const startsSection = block.kind === 'h1' && used > max * 0.55;
    if (page.length && (used + w > max || startsSection)) flush();
    page.push(block);
    used += w;
  });
  if (page.length) pages.push(page);
  return pages;
};

/** Page index that contains `anchor`, or -1. */
export const findAnchorPage = (pages: DocBlock[][], anchor: string) =>
  pages.findIndex(p => p.some(b => b.anchors?.includes(anchor)));

/** Top-level sections and the page each one starts on. */
export const sectionIndex = (pages: DocBlock[][]) =>
  pages.flatMap((p, page) =>
    p
      .filter(b => b.kind === 'h1' && !b.continued && blockText(b).trim())
      .map(b => ({ label: blockText(b).trim(), page }))
  );

/** Plain strings (rules, tips) → list blocks. */
export const listToBlocks = (items: string[]): DocBlock[] =>
  items.map(text => ({ kind: 'li', inlines: [{ text }] }));
